import { Button } from '../../shared/ui/Button';

import s from './NoAccessScreen.module.css';
import { signOutAndReset } from './sign-out';

export type NoAccessReason = 'pending_approval' | 'not_configured' | 'unknown';

interface NoAccessScreenProps {
  reason: NoAccessReason;
}

interface Copy {
  title: string;
  body: string;
  next: string;
}

const COPY: Record<NoAccessReason, Copy> = {
  pending_approval: {
    title: 'Your institute is awaiting approval',
    body: 'You have applied to register an institute and an administrator is reviewing it. You will be able to sign in to your dashboard as soon as it is approved.',
    next: 'Approvals are usually reviewed within one working day.',
  },
  not_configured: {
    title: "We couldn't place your account yet",
    body: 'Your sign-in worked, but this platform has no default institute set up for new accounts, so there is nowhere to enrol you.',
    next: 'Ask the platform administrator to choose a default institute, then sign in again.',
  },
  unknown: {
    title: "We couldn't place your account yet",
    body: 'Your account is not attached to an institute, so there is no dashboard to show you.',
    next: 'Ask your institute to invite you, then sign in again.',
  },
};

/**
 * The end of the line for an account with no role.
 *
 * Two things this replaces, both of which were real dead ends: the old copy
 * ("Ask your institute owner to invite you") was the SAME message whether the
 * user's institute application was pending or the platform simply had nothing
 * configured — one is a wait, the other needs an administrator. And because the
 * old screens rendered instead of the app shell, the only LogoutButton in the
 * app never mounted, so the user could not even sign out.
 */
export function NoAccessScreen({ reason }: NoAccessScreenProps): JSX.Element {
  // eslint-disable-next-line security/detect-object-injection -- `reason` is a literal union, not user input
  const copy = COPY[reason];

  return (
    <main className={s.shell}>
      <div className={s.card}>
        <span className={s.mark} aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5M12 16h.01" />
          </svg>
        </span>

        <h1 className={s.title}>{copy.title}</h1>
        <p className={s.body}>{copy.body}</p>
        <p className={s.next}>{copy.next}</p>

        <div className={s.actions}>
          <Button
            variant="secondary"
            onClick={() => {
              void signOutAndReset();
            }}
          >
            Sign out
          </Button>
        </div>
      </div>
    </main>
  );
}
